import { useEffect } from 'react';
import * as Sentry from '@sentry/react';
import { log, logAttributes } from '@root/app/shared/new-services/logging.service';
import { authService } from '@root/app/shared/new-services/auth.service';
import { companyService } from '@root/app/shared/new-services/company.service';
import { analyticsService } from '@root/app/shared/new-services/analytics.service';
import { startStatusPolling, stopStatusPolling } from '@root/app/shared/new-services/company_status.service';
import { intercomService } from '@root/app/shared/new-services/intercom.service';
import { startSegmentationPrefetch } from '@root/app/shared/new-services/segmentationPrefetch.service';

export const useSessionBootstrap = (isAuthenticated: boolean): void => {
    useEffect(() => {
        if (!isAuthenticated) {
            stopStatusPolling();
            return;
        }

        companyService.getUserCompany((err, company) => {
            if (err || !company) {
                return;
            }

            const user = authService.user();
            analyticsService.identifyUserContext({
                userId: user.id,
                email: user.email,
                name: user.name,
                role: user.role,
                companyId: company.id,
                companyName: company.name
            });

            intercomService.boot({
                user_id: user.id,
                email: user.email,
                companies: [{ company_id: company.id }]
            });

            Sentry.setUser({ id: user.id, email: user.email });

            // Example: attributes set on the isolation scope ride along on *every*
            // subsequent log, span and error in this session, so individual call sites
            // never have to remember to pass company or role. Prefer this over repeating
            // the same three attributes in twenty `log.info` calls.
            //
            // Scope attributes accept string | number | boolean only — no objects.
            Sentry.getIsolationScope().setAttributes({
                'company.id': company.id,
                'company.name': company.name,
                'user.role': user.role
            });

            // Example: a business milestone. `info` is the right level — this is the
            // normal, successful path, and it's the event you'll filter on when someone
            // reports "the dashboard was empty when I logged in this morning".
            log.info('Session bootstrapped', logAttributes({
                'user.role': user.role,
                'company.id': company.id,
                'app.locale': authService.getLanguage()
            }));

            startSegmentationPrefetch();
            startStatusPolling();
        });
    }, [isAuthenticated]);

    useEffect(() => {
        if (!isAuthenticated) {
            return;
        }

        const onResize = () => intercomService.updateDisplayForViewport();
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, [isAuthenticated]);
};
